import React from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';

import styles from '../AssetSizes/AssetSizes.module.scss';
import limitedUseStyles from './LimitedUseProducts.module.scss';
import PriceInfo from '../AssetSizes/PriceInfo/PriceInfo.tsx';
import assetSizeLabel from '../../../support/assetSizeLabel';
import useSelectedLimitedUseProduct from '../../../hooks/stateful/useSelectedLimitedUseProduct';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex.ts';
import useOnLoad from '../../../hooks/queries/useOnLoad';

const LimitedUseProducts = () => {
  const t = useTranslations();
  const theme = useTheme();
  const themeRadio = theme.radio || {};
  const { selectedSize: size } = useSelectedIndex();
  const { selectedLimitedUseProduct, setSelectedLimitedUseProduct } =
    useSelectedLimitedUseProduct();

  const { response: { limitedUseProducts } = {} } = useOnLoad();

  const moreThanOneProduct = limitedUseProducts?.length > 1;

  const updateLimitedUseProductselection = (usage) => {
    if (selectedLimitedUseProduct === usage) {
      return;
    }
    setSelectedLimitedUseProduct(usage);
  };

  const renderLimitedUseProducts = () =>
    limitedUseProducts.map((limitedUseProduct) => {
      const { usage } = limitedUseProduct;
      const isSelected = selectedLimitedUseProduct === usage;
      /* Not sure how passing limitedUseProduct into PricingInfo worked before as LimitedUseProduct only had 4 props
       * while PricingInfo requires a size to be of type imageSizeType or filmSizeType. To pass propType validation, and
       * because we need access to things like currencyCode merge the limitedUseProduct into size  */

      const limitedUseProductSize = { ...size, ...limitedUseProduct };
      return (
        <label
          key={usage}
          className={cx(
            moreThanOneProduct ? themeRadio.radio : '',
            isSelected ? styles.selectedSizeItem : styles.sizeItem
          )}
        >
          <div className={cx(limitedUseStyles.itemDetails, styles.itemDetails)}>
            <input
              className={cx(
                moreThanOneProduct ? themeRadio.radioButton : styles.noRadio
              )}
              data-testid={usage}
              type="radio"
              checked={isSelected}
              onChange={() => updateLimitedUseProductselection(usage)}
              id={size.label}
            />
            <div className={styles.description}>
              <span
                className={styles.name}
                htmlFor={size.label}
              >
                {t(`limited_use_name_${usage.toLowerCase()}`)}
              </span>
              <div className={limitedUseStyles.productDescription}>
                {t(`limited_use_description_${usage.toLowerCase()}`)}
              </div>
              {isSelected && (
                <div className={styles.sizeDetails}>
                  <div className={styles.info}>
                    {assetSizeLabel(t, size)}
                    <span className={styles.seperator}>|</span>
                    <span>
                      {t('img_size_in_pixels', {
                        width: size.pixelWidth,
                        height: size.pixelHeight,
                      })}{' '}
                      (
                      {t('img_size_in_units', {
                        width: size.localeWidth,
                        height: size.localeHeight,
                        units: t(`${size.unit}`),
                      })}
                      )
                    </span>
                  </div>
                  <div className={styles.info}>
                    {size.dpi} dpi
                    <div className={styles.seperator}>|</div>
                    {t('img_size_in_megapixels', { size: size.megapixels })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <PriceInfo
            size={limitedUseProductSize}
            currencyCode={limitedUseProductSize.currencyCode}
          />
        </label>
      );
    });

  return (
    <div
      className={styles.table}
      data-testid="limited-use-products"
    >
      {limitedUseProducts?.length && size && renderLimitedUseProducts()}
    </div>
  );
};

export default LimitedUseProducts;
